import { useState } from 'react';
import type { ReactNode } from 'react';
import type { TenantStatus } from '@yourstore/domain';
import { useAction, useApi } from '../../context';
import { Failure, Rich } from '../../bits';
import { Panel, useToast } from '../../kit';
import { stageMove, stageName } from '../../../vocabulary';
import { useT } from '../../../messages';
import { Dialog } from './Dialog';

/**
 * **Close this business** — the danger zone (console redesign B2), where the
 * plan puts offboarding: at the bottom of Setup, in red, behind a confirm that
 * has to be typed.
 *
 * Offboarding is the one stage move that is not a step in the journey but the
 * end of it, so it asks for more than a click: the operator types the
 * business's id, as `yourstore tenant delete --confirm <id>` does for the
 * delete that follows it (docs/runbooks/offboarding.md). The panel and the
 * dialog both say what the runbook says happens around it — the export and
 * the hand-over, that nothing is deleted by offboarding, and, for a protected
 * business, that nothing can be deleted at all until protection is removed
 * from the command line.
 *
 * The header's "Change stage" menu keeps its own Offboard entry: this panel
 * is where an operator looks for it, the menu is where the other four moves
 * are, and removing it there would make the one move that is a door out
 * the only one missing from the list of moves.
 */
export function CloseBusiness({
  tenantId,
  name,
  status,
  isProtected,
  onChanged,
}: {
  tenantId: string;
  name: string;
  status: TenantStatus;
  isProtected: boolean;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const action = useAction();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const move = stageMove(t, 'offboarded', name);

  const offboard = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setStatus(tenantId, 'offboarded');
      toast(t('stage.toast', { name, stage: stageName(t, answer.status) }));
    });
    if (ok) {
      setOpen(false);
      onChanged();
    }
  };

  const done = status === 'offboarded';

  return (
    <Panel title={t('setup.close.title')} tone="danger">
      {done ? (
        <p className="lede">{t('setup.close.already', { name })}</p>
      ) : (
        <>
          <p className="lede">{t('setup.close.lede', { name })}</p>
          <p className="lede">
            {isProtected ? t('setup.close.protected', { name }) : t('setup.close.unprotected')}
          </p>
          <div>
            <button
              type="button"
              className="button button--danger"
              onClick={() => {
                action.clear();
                setTyped('');
                setOpen(true);
              }}
            >
              {t('setup.close.button')}
            </button>
          </div>
        </>
      )}

      {open && move !== null ? (
        <Dialog
          title={move.title}
          busy={action.busy}
          onClose={() => {
            action.clear();
            setOpen(false);
          }}
          footer={
            <button
              type="button"
              className="button button--danger"
              disabled={typed.trim() !== tenantId || action.busy}
              onClick={() => void offboard()}
            >
              {action.busy ? t('common.working') : move.confirm}
            </button>
          }
        >
          <p className="modal__consequence">
            <Rich text={move.consequence} />
          </p>
          <p className="modal__consequence">{t('setup.close.handover')}</p>
          <p className="modal__consequence">
            {isProtected ? t('setup.close.protected', { name }) : t('setup.close.unprotected')}
          </p>
          <label className="field">
            <span className="field__label">{t('setup.close.type', { id: tenantId })}</span>
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
          {action.failure !== null ? <Failure failure={action.failure} /> : null}
        </Dialog>
      ) : null}
    </Panel>
  );
}
